import { useEffect, useRef, useState } from 'react'

import type { Reaction } from '../../api/types'
import { SmileyIcon } from '../../components/Icons'
import { useOnline } from '../../hooks/useOutbox'
import { Emojiwahl } from './Emojiwahl'

/**
 * Was zur Auswahl steht — dieselbe Liste wie im Server.
 *
 * Eine feste Auswahl statt einer vollständigen Emoji-Tastatur: sie passt in
 * eine Zeile, ist mit dem Daumen zu treffen, und in der Datenbank landet
 * nichts, was niemand erwartet hat.
 */
export const EMOJI = ['❤️', '👍', '😍', '😂', '😮', '🎉', '👏', '🙏']

/**
 * Die Reaktionen zu einem Ziel — einer Nachricht oder einem Foto.
 *
 * Angezeigt wird, was der Server geschickt hat. Er entscheidet auch, wessen
 * Namen dabeistehen: ein Empfänger sieht die Reaktionen der anderen nicht,
 * sonst verriete ein Beitrag mit drei Herzen, wie viele mitlesen.
 */
export function Reactions({
  reactions,
  onPick,
  busy,
  compact,
  imFluss,
  nurAnzeigen,
}: {
  reactions: Reaction[]
  onPick: (emoji: string) => void
  busy?: boolean
  /** Über einem Foto: kleiner und auf dunklem Grund. */
  compact?: boolean
  /**
   * Die Auswahl steht **im Fluss** statt zu schweben.
   *
   * **Aus dem Betrieb, und es war ein selbst gebauter Fehler:** seit die
   * Auswahl ein Raster ist, ist sie 152 statt 56 Punkte hoch. Im
   * Highlight-Blatt scrollt der Inhalt in einem Kasten von 126 Punkten
   * (`.bogen__blatt`, `overflow: auto`) — nach oben aufgeklappt ragte sie
   * oben heraus, wurde dort **abgeschnitten**, und die acht Zeichen lagen
   * genau im abgeschnittenen Teil. Angetippt hat man die Landkarte
   * dahinter. Gemessen, nicht vermutet: `elementFromPoint` lieferte den
   * Leaflet-Kasten.
   *
   * Nach unten aufgeklappt lief sie stattdessen aus dem Fenster: sie passte
   * in keine Richtung. Im Fluss schiebt sie den Inhalt auseinander, der
   * Kasten wird länger, und man scrollt zu ihr — nichts wird abgeschnitten.
   * Wo ein Scrollkasten ist, weiß das Bauteil, das sie einsetzt; der Browser
   * weiß es nicht von allein.
   */
  imFluss?: boolean
  /**
   * Nur zeigen, was schon dasteht — **ohne den Knopf zum Reagieren**.
   *
   * An der Sprechblase liegt die Auswahl seit #8 im Menü beim langen
   * Drücken, wie bei iMessage und WhatsApp. Der Smiley-Knopf saß dort als
   * helle Scheibe mitten auf der blauen Blase und war das Lauteste darin;
   * die vergebenen Reaktionen bleiben, denn die sind Inhalt und kein
   * Bedienelement. Am Foto und am Highlight gibt es kein solches Menü — dort
   * bleibt der Knopf.
   */
  nurAnzeigen?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [alle, setAlle] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  // **Ohne Netz abgeschaltet, nicht stumm.** Ein Emoji ist ein Schreibzugriff
  // wie jeder andere; vorher passierte beim Antippen schlicht nichts, ohne
  // ein Wort -- die einzige Stelle der Anwendung, die geschwiegen hat.
  const online = useOnline()
  const gesperrt = busy || !online
  const hinweis = online ? undefined : 'Ohne Netz geht das gerade nicht.'

  useEffect(() => {
    if (!open) return
    function outside(event: MouseEvent) {
      if (!box.current?.contains(event.target as Node)) {
        setOpen(false)
        setAlle(false)
      }
    }
    // Nächster Tick: der Klick, der geöffnet hat, soll nicht gleich schließen.
    const id = setTimeout(() => document.addEventListener('click', outside), 0)
    return () => {
      clearTimeout(id)
      document.removeEventListener('click', outside)
    }
  }, [open])

  return (
    <div
      ref={box}
      className={compact ? 'reactions reactions--compact' : 'reactions'}
      onClick={(event) => event.stopPropagation()}
    >
      {reactions.map((reaction) => (
        <button
          key={reaction.emoji}
          type="button"
          className={reaction.mine ? 'reaction reaction--mine' : 'reaction'}
          disabled={gesperrt}
          onClick={() => onPick(reaction.emoji)}
          // Die Namen kommen vom Server -- steht dort nichts, steht hier auch
          // nichts.
          title={hinweis ?? reaction.who.join(', ')}
        >
          <span aria-hidden="true">{reaction.emoji}</span>
          {reaction.count > 1 && <span className="reaction__count">{reaction.count}</span>}
          <span className="visually-hidden">
            {reaction.who.length > 0 ? reaction.who.join(', ') : `${reaction.count}`}
          </span>
        </button>
      ))}

      {!nurAnzeigen && (
      <button
        type="button"
        className="reaction reaction--add"
        disabled={gesperrt}
        onClick={() => setOpen((value) => !value)}
        aria-label="Mit einem Emoji reagieren"
        aria-expanded={open}
        title={hinweis ?? 'Reagieren'}
      >
        <SmileyIcon size={compact ? 14 : 15} />
      </button>
      )}

      {/* Die Auswahl hängt am Knopf: ohne ihn kann `open` nie wahr werden. */}
      {open && !alle && (
        <div className={imFluss ? 'picker picker--inline' : 'picker'} role="menu">
          {EMOJI.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="picker__item"
              role="menuitem"
              onClick={() => {
                onPick(emoji)
                setOpen(false)
              }}
            >
              {emoji}
            </button>
          ))}

          {/* **Der Weg zu allem anderen — und er führt nicht mehr über die
              Tastatur.** Die erste Fassung öffnete ein Feld, damit die
              Emoji-Tastatur des Geräts aufgeht. Sie geht auch auf, aber auf
              der Buchstaben-Ebene: welche Ebene eine Tastatur zeigt, ist
              nicht zu setzen, dafür gibt es keine Schnittstelle. Aus dem
              Betrieb: „man muss manuell auf Emoji umschalten." Also eine
              eigene Auswahl, die auf jedem Gerät gleich aussieht. */}
          <button
            type="button"
            className="picker__item picker__mehr"
            role="menuitem"
            onClick={() => setAlle(true)}
            aria-label="Mehr Emoji"
            title="Mehr Emoji"
          >
            ＋
          </button>
        </div>
      )}

      {open && alle && (
        <Emojiwahl
          imFluss={imFluss}
          onPick={onPick}
          onZu={() => {
            setOpen(false)
            setAlle(false)
          }}
        />
      )}
    </div>
  )
}
